<template>
  <b-card no-body>
    <b-card-header>
      <b-card-title>{{ $t('adherenceByProcess') }}</b-card-title>
    </b-card-header>

    <b-card-body>
      <chartjs-component-bar-chart
        :height="420"
        :data="this.series"
        :options="latestBarChart.options"
      />
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardHeader, BCardBody, BCardTitle, BCardSubTitle } from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import ChartjsComponentBarChart from '@/views/habit/charts-components/ChartjsComponentBarChart.vue'
import { $themeColors } from '@themeConfig'
import i18n from '@/libs/i18n'
import { EventBus } from "@/views/habit/EventBus.js"

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BCardTitle,
    BCardSubTitle,
    flatPickr,
    ChartjsComponentBarChart,
  },
  props: {
    processData: {
      type: Object,
      required: true,
    },
    emitToParentComponent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      latestBarChart: {
        // data: {
        //   labels: ['7/12', '8/12', '9/12', '10/12', '11/12', '12/12', '13/12', '14/12', '15/12', '16/12', '17/12'],
        //   datasets: [
        //     {
        //       data: [275, 90, 190, 205, 125, 85, 55, 87, 127, 150, 230, 280, 190],
        //       backgroundColor: '#28dac6',
        //       borderColor: 'transparent',
        //     },
        //   ],
        // },
        options: {
          onHover: (event, chartElement) => {
            event.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
          },
          onClick: (c, i) => {
            let e = i[0];
            if (!e) return
            // var x_value = this.data.labels[e._index];
            // var y_value = this.data.datasets[0].data[e._index];
            const processId = this.series._additionalInfo.processIds[e._index]
            if (this.emitToParentComponent) this.$emit('filterByProcess', processId)
            else EventBus.$emit("filterByProcess", processId)
          },
          elements: {
            rectangle: {
              borderWidth: 2,
              borderSkipped: 'bottom',
            },
          },
          responsive: true,
          maintainAspectRatio: false,
          responsiveAnimationDuration: 500,
          legend: {
            // display: false,
            position: 'top',
            align: 'center',
            labels: {
              usePointStyle: true,
              padding: 25,
              boxWidth: 9,
            },
          },
          tooltips: {
            // Updated default tooltip UI
            shadowOffsetX: 1,
            shadowOffsetY: 1,
            shadowBlur: 8,
            shadowColor: 'rgba(0, 0, 0, 0.25)',
            backgroundColor: $themeColors.light,
            titleFontColor: $themeColors.dark,
            bodyFontColor: $themeColors.dark,
            callbacks: {
              title: function(tooltipItem, data) {
                const index = tooltipItem[0].index
                const completeRiskName = data._additionalInfo.completeLabels[index]
                return completeRiskName
              },
              label: function(tooltipItem, data) {
                return data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] + '%'
              }
            },
          },
          scales: {
            xAxes: [
              {
                display: true,
                gridLines: {
                  display: true,
                  color: 'rgba(200, 200, 200, 0.2)',
                  zeroLineColor: 'rgba(200, 200, 200, 0.2)',
                },
                scaleLabel: {
                  display: false,
                },
                ticks: {
                  fontColor: '#6e6b7b',
                  // Show only the code (first 4 characters of the label)
                  callback: function(value, index, values) {
                    return value.length > 4 ? value.substr(0, 4) + '...' : value;
                  }
                },
              },
            ],
            yAxes: [
              {
                display: true,
                gridLines: {
                  color: 'rgba(200, 200, 200, 0.2)',
                  zeroLineColor: 'rgba(200, 200, 200, 0.2)',
                },
                ticks: {
                  stepSize: 25,
                  min: 0,
                  max: 100,
                  fontColor: '#6e6b7b',
                  callback: function(value, index, values) {
                    return value + '%';
                  }
                },
                scaleLabel: {
                  display: true,
                  labelString: i18n.t('label.adherence'),
                  fontSize: 14
                },
              },
            ],
          },
          // 👇 Necesitamos que Chart.js reciba estos eventos
          events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],

          // Limpieza al redimensionar (evita duplicar listeners/tooltips)
          onResize: function(chart) {
            const canvas = chart.canvas
            if (canvas && canvas._labelTooltipCleanup) canvas._labelTooltipCleanup()
          },

          // Después del primer render, instalamos listeners y el tooltip flotante
          animation: {
            onComplete: function() {
              const chart = this.chart || this     // Chart.js 2 compat
              const canvas = chart.canvas
              const parent = canvas.parentNode
              if (!parent) return

              // Evita reinstalar si ya está
              if (canvas._labelTooltipAttached) return
              canvas._labelTooltipAttached = true

              // Aseguramos posicionamiento relativo para ubicar el div
              if (!getComputedStyle(parent).position || getComputedStyle(parent).position === 'static') {
                parent.style.position = 'relative'
              }

              // Tooltip flotante
              const tip = document.createElement('div')
              tip.style.position = 'absolute'
              tip.style.pointerEvents = 'none'
              tip.style.padding = '6px 8px'
              tip.style.borderRadius = '6px'
              tip.style.boxShadow = '0 4px 12px rgba(0,0,0,.15)'
              tip.style.background = 'rgba(255,255,255,.95)'
              tip.style.border = '1px solid rgba(0,0,0,0.08)'
              tip.style.font = '12px system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif'
              tip.style.whiteSpace = 'nowrap'
              tip.style.opacity = '0'
              tip.style.transform = 'translate(-50%, -120%)'
              parent.appendChild(tip)

              const moveHandler = function(evt) {
                const rect = canvas.getBoundingClientRect()
                const mouseX = evt.clientX - rect.left
                const mouseY = evt.clientY - rect.top

                const xAxis = chart.scales['x-axis-0']
                const withinX = mouseX >= xAxis.left && mouseX <= xAxis.right
                // Franja de labels = debajo del área de plot y arriba del borde inferior del eje
                const withinY = mouseY >= chart.chartArea.bottom && mouseY <= xAxis.bottom

                if (!withinX || !withinY) {
                  tip.style.opacity = '0'
                  return
                }

                // Índice del tick bajo el mouse
                let idx
                if (typeof xAxis.getValueForPixel === 'function') {
                  const v = xAxis.getValueForPixel(mouseX)
                  idx = (typeof v === 'number') ? Math.round(v) : (xAxis.ticks || []).indexOf(v)
                }
                if (idx == null || idx < 0 || idx >= (xAxis.ticks || []).length) {
                  // Fallback por si getValueForPixel no devuelve un número
                  const ticks = xAxis.ticks || []
                  const step = (xAxis.right - xAxis.left) / (ticks.length || 1)
                  idx = Math.floor((mouseX - xAxis.left) / step)
                }

                const fullLabels = chart.config.data._additionalInfo && chart.config.data._additionalInfo.completeLabels
                const text = fullLabels && fullLabels[idx]
                if (!text) {
                  tip.style.opacity = '0'
                  return
                }

                tip.textContent = text
                tip.style.left = mouseX + 'px'
                tip.style.top = mouseY + 'px'
                tip.style.opacity = '1'
              }

              const outHandler = function() { tip.style.opacity = '0' }

              canvas.addEventListener('mousemove', moveHandler)
              canvas.addEventListener('mouseleave', outHandler)

              // Guardamos cleanup para evitar fugas y reinstalaciones
              canvas._labelTooltipCleanup = function() {
                canvas.removeEventListener('mousemove', moveHandler)
                canvas.removeEventListener('mouseleave', outHandler)
                if (tip && tip.parentNode) tip.parentNode.removeChild(tip)
                canvas._labelTooltipAttached = false
                canvas._labelTooltipCleanup = null
              }
            }
          }
        },
      },
      // rangePicker: ['2019-05-01', '2019-05-10'],
    }
  },
  computed: {
    series() {
      return {
        labels: this.processData.labels,
        datasets: [
          {
            data: this.processData.data.adherence,
            label: i18n.t('label.adherence'),
            backgroundColor: $themeColors.info,
            borderColor: 'transparent',
            barThickness: 15,
          },
          // {
          //   type: 'line',
          //   data: this.processData.data.confirmations,
          //   label: i18n.t('label.confirmations'),
          //   yAxisID: 'right-y-axes',
          //   backgroundColor: $themeColors.warning,
          //   borderColor: $themeColors.warning,
          //   lineTension: 0,
          //   fill: false,
          //   pointRadius: 5,
          //   order: 1,
          // },
        ],
        _additionalInfo: {
          processIds: this.processData.additionalInfo.ids,
          completeLabels: this.processData.additionalInfo.completeLabels
        }
      }
    },
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
